import { InfoproductsAction } from './infoproducts.action';
import {
  IAvailableCourse,
  ICustomerCourse
} from '../../interfaces/infoproducts/course.interfaces';
import {
  IAvailableStrategy,
  ICustomerStrategy
} from '../../interfaces/infoproducts/strategy.interfaces';
import {
  IProductCategory,
  IProductGroup,
  IProductSpecialist
} from '../../interfaces/infoproducts/common.interfaces';

const RECOMMENDATION_TAG_PATTERN = /^r-*[a-z\d-]*[a-z\w]$/;

export interface IInfoproductsState {
  error: string;

  // Trial
  trialRequestError: string;
  pendingTrialRequest: boolean;

  isLoadingCustomerProducts: boolean;

  // Courses
  courseGroups: Map<string, IProductGroup>;
  courseTeachers: Map<string, IProductSpecialist>;
  customerCourses: Map<string, ICustomerCourse>;
  isLoadingCourses: boolean;
  availableCourses: IAvailableCourse[];
  courseCategories: Map<string, IProductCategory>;

  // Strategies
  strategyGroups: Map<string, IProductGroup>;
  customerStrategies: Map<string, ICustomerStrategy>;
  strategyCategories: Map<string, IProductCategory>;
  isLoadingStrategies: boolean;
  availableStrategies: IAvailableStrategy[];
  strategySpecialists: Map<string, IProductSpecialist>;
  allowedRecommendations: Map<string, string>;
  isSavingLesson: boolean;
}

export const initialState: IInfoproductsState = {
  error: null,
  isLoadingCustomerProducts: false,

  trialRequestError: null,
  pendingTrialRequest: false,

  courseGroups: new Map(),
  courseTeachers: new Map(),
  customerCourses: new Map(),
  courseCategories: new Map(),
  isLoadingCourses: false,

  strategyGroups: new Map(),
  availableCourses: [],
  customerStrategies: new Map(),
  strategyCategories: new Map(),
  availableStrategies: [],
  isLoadingStrategies: false,
  strategySpecialists: new Map(),
  allowedRecommendations: new Map(),

  isSavingLesson: false
};

export function InfoproductsReducer(
  state = initialState,
  action?: InfoproductsAction
): IInfoproductsState {
  switch (action.type) {
    case InfoproductsAction.setLessonComplete:
      return {
        ...state,
        isSavingLesson: true
      };

    case InfoproductsAction.setLessonCompleteSuccess:
      return {
        ...state,
        isSavingLesson: false
      };
    case InfoproductsAction.setLessonCompleteError:
      return {
        ...state,
        isSavingLesson: false,
        error: action.payload
      };

    case InfoproductsAction.getAvailableCourses:
      return {
        ...state,
        isLoadingCourses: true
      };

    case InfoproductsAction.getAvailableCoursesSuccess: {
      const courseTeachers = new Map<string, IProductSpecialist>();
      const availableCourses: IAvailableCourse[] = action.payload.map(
        ({ teacher, ...course }: IAvailableCourse) => {
          if (teacher) courseTeachers.set(teacher.id, teacher);
          return {
            ...course,
            get teacher() {
              return teacher && courseTeachers.get(teacher.id);
            }
          };
        }
      );

      return {
        ...state,
        courseTeachers,
        availableCourses,
        isLoadingCourses: false
      };
    }

    case InfoproductsAction.getAvailableCoursesError:
      return {
        ...state,
        error: action.payload,
        isLoadingCourses: false
      };

    case InfoproductsAction.getAvailableStrategies:
      return {
        ...state,
        isLoadingStrategies: true
      };

    case InfoproductsAction.getAvailableStrategiesSuccess: {
      const strategySpecialists = new Map<string, IProductSpecialist>();
      const availableStrategies: IAvailableStrategy[] = action.payload.map(
        ({ specialists, ...strategy }: IAvailableStrategy) => {
          const specialistsId = (specialists || []).map(
            s => strategySpecialists.set(s.id, s) && s.id
          );
          return {
            ...strategy,
            get specialists() {
              return specialistsId.map(id => strategySpecialists.get(id));
            }
          };
        }
      );

      return {
        ...state,
        strategySpecialists,
        availableStrategies,
        isLoadingStrategies: false
      };
    }

    case InfoproductsAction.getAvailableStrategiesError:
      return {
        ...state,
        error: action.payload,
        isLoadingStrategies: false
      };

    case InfoproductsAction.getCustomerProducts:
      return {
        ...state,
        isLoadingCustomerProducts: true
      };

    case InfoproductsAction.getCustomerProductsSuccess: {
      const courses: ICustomerCourse[] = action.payload.courses;
      const strategies: ICustomerStrategy[] = action.payload.strategies;
      const customerCourses = new Map<string, ICustomerCourse>();
      const customerStrategies = new Map<string, ICustomerStrategy>();
      const allowedRecommendations = new Map<string, string>();

      courses.forEach(course => customerCourses.set(course.id, course));
      strategies.forEach(strategy => {
        customerStrategies.set(strategy.id, strategy);
        strategy.tags.forEach(tag => {
          if (RECOMMENDATION_TAG_PATTERN.test(tag)) {
            allowedRecommendations.set(tag.replace('r-', ''), strategy.id);
          }
        });
      });

      return {
        ...state,
        customerCourses,
        customerStrategies,
        allowedRecommendations,
        isLoadingCustomerProducts: false
      };
    }

    case InfoproductsAction.getCustomerProductsError:
      return {
        ...state,
        error: action.payload,
        isLoadingCourses: false
      };

    case InfoproductsAction.trialRecommendation:
      return {
        ...state,
        pendingTrialRequest: true,
        trialRequestError: null
      };

    case InfoproductsAction.trialRecommendationError:
      return {
        ...state,
        pendingTrialRequest: false,
        trialRequestError: action.payload
      };

    case InfoproductsAction.trialRecommendationSuccess:
      return {
        ...state,
        pendingTrialRequest: false,
        trialRequestError: null
      };

    default:
      return state;
  }
}
